import React, { useState } from 'react'
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  VStack,
  HStack,
  Tag,
  Image,
  Button,
  useColorModeValue,
  Divider,
} from '@chakra-ui/react'
import { ChevronRightIcon } from '@chakra-ui/icons'
import { Link as RouterLink } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { POSTS } from '../data/posts'
import { LazyImage } from '../components/LazyImage'
import { pageTitle } from '../global'
import { useTranslation } from 'react-i18next'

const BlogPage: React.FC = () => {
  const { t } = useTranslation()
  const blogPosts = POSTS
  const [currentPage, setCurrentPage] = useState(1)
  const postsPerPage = 6

  const bgColor = useColorModeValue('gray.50', 'gray.900')
  const cardBgColor = useColorModeValue('white', 'gray.800')
  const textColor = useColorModeValue('gray.600', 'gray.300')
  const headingColor = useColorModeValue('purple.600', 'purple.300')
  const resultBgColor = useColorModeValue('white', 'gray.800')

  const indexOfLastPost = currentPage * postsPerPage
  const indexOfFirstPost = indexOfLastPost - postsPerPage
  const currentPosts = blogPosts.slice(indexOfFirstPost, indexOfLastPost)

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber)

  return (
    <>
      <Helmet>
        <title>{pageTitle(t('blog.title'))}</title>
        <meta name="description" content={t('blog.description')} />
      </Helmet>
      <Box bg={bgColor} minH="100vh">
        <Container maxW="container.xl" py={10}>
          <VStack spacing={8} align="stretch">
            <Heading as="h1" size="2xl" textAlign="center" color={headingColor}>
              {t('blog.pageTitle')}
            </Heading>
            <Text fontSize="xl" textAlign="center" color={textColor}>
              {t('blog.subtitle')}
            </Text>
          </VStack>
        </Container>
        <Box bg={resultBgColor} py={10}>
          <Container maxW="container.xl">
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8}>
              {currentPosts.map((post) => (
                <Box
                  key={post.id}
                  bg={cardBgColor}
                  borderRadius="lg"
                  overflow="hidden"
                  boxShadow="md"
                >
                  <LazyImage
                    src={post.image}
                    alt={post.title}
                    width="100%"
                    height={200}
                    objectFit="cover"
                  />
                  <VStack p={5} align="start" spacing={3}>
                    <Heading as="h3" size="md" color={headingColor}>
                      {post.title}
                    </Heading>
                    <Text color={textColor} noOfLines={3}>
                      {post.excerpt}
                    </Text>
                    <HStack justify="flex-end" w="100%">
                      <Button
                        as={RouterLink}
                        to={`/blog/${post.id}`}
                        rightIcon={<ChevronRightIcon />}
                        colorScheme="purple"
                        variant="outline"
                        size="sm"
                      >
                        Čítať viac
                      </Button>
                    </HStack>
                  </VStack>
                </Box>
              ))}
            </SimpleGrid>
          </Container>
        </Box>
        {blogPosts.length > postsPerPage && (
          <HStack justify="center">
            {Array.from({
              length: Math.ceil(blogPosts.length / postsPerPage),
            }).map((_, index) => (
              <Button
                key={index}
                onClick={() => paginate(index + 1)}
                colorScheme={currentPage === index + 1 ? 'purple' : 'gray'}
              >
                {index + 1}
              </Button>
            ))}
          </HStack>
        )}
        <Container maxW="container.xl" py={10}>
          <Box>
            <Heading as="h2" size="lg" mb={4} color={headingColor}>
              {t('blog.discover.title')}
            </Heading>
            <Text color={textColor} mb={4}>
              {t('blog.discover.intro')}
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <VStack align="start">
                <Text color={textColor}>
                  • {t('blog.discover.topics.interpretation')}
                </Text>
                <Text color={textColor}>
                  • {t('blog.discover.topics.lucid')}
                </Text>
                <Text color={textColor}>
                  • {t('blog.discover.topics.health')}
                </Text>
              </VStack>
              <VStack align="start">
                <Text color={textColor}>
                  • {t('blog.discover.topics.culture')}
                </Text>
                <Text color={textColor}>
                  • {t('blog.discover.topics.science')}
                </Text>
                <Text color={textColor}>
                  • {t('blog.discover.topics.tips')}
                </Text>
              </VStack>
            </SimpleGrid>
            <Text color={textColor} mt={4}>
              {t('blog.discover.outro')}
            </Text>
          </Box>
        </Container>
      </Box>
    </>
  )
}

export default BlogPage
